// Export components
export { default as DocumentUpload } from './components/DocumentUpload';
export { default as UploadModal } from './components/UploadModal';
export { default as DocumentsPage } from './components/DocumentsPage';
export { default as DocumentDetailsPage } from './components/DocumentDetailsPage';
export { default as ManageAssociationsModal } from './components/ManageAssociationsModal';

// Export services
export { documentProcessingService } from './services/documentProcessingService';
export { entityRecognitionService } from './services/entityRecognitionService';
export { configService } from './services/configService';
export { documentService } from './services/documentService';

// Export configuration
export { DOCUMENT_TAXONOMY } from './config/documentTaxonomy.js';
export { AI_PROMPTS } from './config/aiPrompts.js';

// Import for initialization
import { documentService } from './services/documentService';
import { backupRegistry } from '@trip-planner-monorepo/shared';
import { db, storage } from '@trip-planner-monorepo/shared';
import { doc, setDoc, collection, getDocs, ref, getDownloadURL, uploadBytes, getBlob } from 'firebase/firestore';
import { ref as storageRef, uploadBytes as storageUpload, getDownloadURL as storageGetURL, getBlob as storageGetBlob } from 'firebase/storage';

// Export initialization function
export const initDocumentsServices = () => {
  // Register backup capabilities with the backup registry
  backupRegistry.register('documents', {
    fetchAll: async (userId, tripId) => {
      console.log(`[Documents] Fetching all documents for backup: userId=${userId}, tripId=${tripId}`);

      // Delegate to documentService to avoid db-binding timing issues and ensure
      // consistent access control (effectiveUserId pattern respected by the service).
      return await documentService.fetchDocuments(userId, tripId);
    },

    restoreAll: async (userId, tripId, data) => {
      console.log(`[Documents] Restoring ${data.length} documents for trip ${tripId}`);

      // Data is an array of document metadata from backup
      for (const docData of data) {
        try {
          // Preserve original ID by using setDoc instead of addDoc
          const docRef = doc(db, 'users', userId, 'trips', tripId, 'documents', docData.id);

          // Remove the id from the data object (it's already in the document reference)
          const { id, ...docDataWithoutId } = docData;

          // Use setDoc to create/overwrite document with preserved ID
          await setDoc(docRef, {
            ...docDataWithoutId,
            userId,  // Ensure userId is set correctly
            tripId,  // Ensure tripId is set correctly
            updatedAt: new Date().toISOString()
          });

          console.log(`[Documents] Restored document ${docData.id} (${docData.name})`);
        } catch (error) {
          console.error(`[Documents] Error restoring document ${docData.id}:`, error);
          throw error;
        }
      }

      console.log(`[Documents] Successfully restored ${data.length} documents metadata`);
    },

    fetchStorage: async (userId, tripId) => {
      console.log(`[Documents] Fetching storage files for backup: userId=${userId}, tripId=${tripId}`);

      // Get all documents to find their storage paths
      const documentsRef = collection(db, 'users', userId, 'trips', tripId, 'documents');
      const snapshot = await getDocs(documentsRef);

      const storageFiles = [];

      for (const docSnapshot of snapshot.docs) {
        const docData = docSnapshot.data();

        if (docData.storagePath && docData.downloadUrl) {
          try {
            // Create storage reference
            const fileRef = storageRef(storage, docData.storagePath);

            // Download file as blob
            const blob = await storageGetBlob(fileRef);

            // Use storagePath as the name for organization
            storageFiles.push({
              name: docData.storagePath,
              blob: blob,
              documentId: docSnapshot.id,
              originalName: docData.name || docData.originalName
            });

            console.log(`[Documents] Fetched storage file: ${docData.name} (${blob.size} bytes)`);
          } catch (error) {
            console.error(`[Documents] Error fetching storage file ${docData.storagePath}:`, error);
            // Continue with other files even if one fails
          }
        }
      }

      console.log(`[Documents] Fetched ${storageFiles.length} storage files`);
      return storageFiles;
    },

    restoreStorage: async (userId, tripId, fileBlobs) => {
      console.log(`[Documents] Restoring ${fileBlobs.length} storage files for trip ${tripId}`);

      for (const fileData of fileBlobs) {
        try {
          // Create storage reference using the original storage path
          const fileRef = storageRef(storage, fileData.name);

          // Upload the blob
          await storageUpload(fileRef, fileData.blob);

          console.log(`[Documents] Restored storage file: ${fileData.originalName || fileData.name}`);
        } catch (error) {
          console.error(`[Documents] Error restoring storage file ${fileData.name}:`, error);
          throw error;
        }
      }

      console.log(`[Documents] Successfully restored ${fileBlobs.length} storage files`);
    },

    version: '1.0'
  });

  console.log('[Documents] Registered with backup registry (with binary file support)');

  // Initialize the document service (starts event listeners)
  documentService.init();
};